import React, { useRef, useState } from 'react';
import MessageInputShell from './MessageInputShell';

const ACCEPTED_MIME = 'application/pdf,image/png,image/jpeg';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

interface Props {
  onSend: (message: string) => void | Promise<void>;
  onAttach: (file: File) => void | Promise<void>;
  /** Context-specific guidance for the shared chat composer. */
  placeholder?: string;
  minimal?: boolean;
  /** When true, input + send + attach are disabled (in-flight turn). */
  disabled?: boolean;
  /**
   * When true, the send button (and attach/textarea) are disabled. The
   * in-flight loading indicator is now rendered as a pseudo message in the
   * chat thread — no spinner is shown on the send button.
   */
  pending?: boolean;
  showAttachments?: boolean;
}

const MessageInput: React.FC<Props> = ({
  onSend,
  onAttach,
  placeholder = 'Ask about the FAR (RFO or Legacy)\u2026',
  disabled,
  pending,
  minimal = false,
  showAttachments,
}) => {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hardDisabled = !!disabled || !!pending;
  const canSendText = text.trim().length > 0;

  const doSend = () => {
    if (hardDisabled || !canSendText) return;
    const msg = text.trim();
    setText('');
    void onSend(msg);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  const onPickFile = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = ev.target.files?.[0];
    // Always clear the input so picking the same file twice still triggers change.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError(`File exceeds 20 MB limit.`);
      return;
    }
    if (
      file.type &&
      !['application/pdf', 'image/png', 'image/jpeg'].includes(file.type)
    ) {
      setError(`Only PDF, PNG, and JPEG files are supported.`);
      return;
    }
    await onAttach(file);
  };

  return (
    <>
      {!minimal && <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME}
        data-testid="farchat-file-input"
        style={{ display: 'none' }}
        onChange={onPickFile}
      />}
      <MessageInputShell
        minimal={minimal}
        value={text}
        onChange={setText}
        onSend={doSend}
        onAttachClick={() => fileInputRef.current?.click()}
        onKeyDown={onKeyDown}
        disabled={hardDisabled}
        placeholder={placeholder}
        error={error}
        showAttachments={showAttachments}
      />
    </>
  );
};

export default MessageInput;
